/**
 * 宮崎県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 *
 * 果物が3品（きんかん・日向夏・マンゴー）あるのは宮崎の看板がそこに集まっているため。
 * 序盤が果物続きにならないよう Lv1・Lv3・Lv5 に離して置いている。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 太陽のタマゴの「糖度15度以上」「重さ350g以上」は県公式に数値が一切ない。書かない。
 *    きんかん「たまたま」だけは県公式に数値があるので書ける。
 *  - チキン南蛮の発祥店・発祥年。農水省は「昭和30年代に延岡市内の洋食店で」までで、
 *    店名は書かない方針。年も特定されていない。
 *  - 辛麺の発祥店・発祥年は不明。国・県・市の公式資料に辛麺の記載がなく、
 *    出典は延岡観光協会になる。麺は「通称こんにゃく麺」で、断定しない。
 *  - 宮崎牛を「A5」「日本一」と書かない。GIの定義は黒毛和種・肉質等級4等級以上・
 *    県内生まれで県内飼養期間最長。全国和牛能力共進会の4大会連続の受賞は事実として書ける。
 *  - 日向夏の発見年は県公式が伝承形。断定しない。
 *  - ねりくりの名の由来は農水省が2説を併記している。
 *  - 冷や汁を「宮崎だけの料理」と書かない。
 *  - みやざき地頭鶏はGI登録産品ではない（宮崎県のGIは宮崎牛・ヤマダイかんしょ・米良糸巻大根）。
 *
 * 本番画像は public/assets/foods/miyazaki/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：ねりくり（店で食べられる品に差し替え）
 * - 鶏の炭火焼（もも焼き）：みやざき地頭鶏の名の由来（藩の地頭職に献上されたから）は県観光協会のページに「と言われています」とあるが、炭火焼そのものの話ではないので書かない
 * - 鶏の炭火焼（もも焼き）：地頭鶏が天然記念物であることも同じ理由で書かない（地頭鶏そのものは鹿児島県との旧島津領で飼われてきた鶏で、料理の名ではない）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { MIYAZAKI_FOOD_HULLS } from './miyazakiFoodShapes.generated';
import { miyazakiSceneryTour } from './miyazakiScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'tamatama',
    level: 1,
    name: '完熟きんかん たまたま',
    shortName: 'たまたま',
    image: '/assets/foods/miyazaki/01_tamatama.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '皮ごと、丸かじりできる。',
    source: 'https://www.pref.miyazaki.lg.jp/contents/org/shoko/appeal/jaja/27_02.html',
    icon: { label: 'たまたま', shape: 'berry', baseColor: '#e8a020', accentColor: '#4a7a3a' },
    reward: {
      unlockScore: 200,
      area: '宮崎県内（施設栽培）',
      headline: '樹の上で熟してから採るきんかん。糖度16度以上・直径2.8センチ以上のものだけ。',
      tip: '皮をむかず、そのまま丸かじりする。生で食べるために育てられている。',
      links: searchLinks('宮崎県', 'きんかん たまたま'),
    },
  },
  {
    id: 'obiten',
    level: 2,
    name: 'おび天',
    shortName: 'おび天',
    image: '/assets/foods/miyazaki/02_obiten.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '豆腐を混ぜるから、やわらかい。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/obiten_miyazaki.html',
    icon: { label: 'おび天', shape: 'dumpling', baseColor: '#d8a850', accentColor: '#8a5a2a' },
    reward: {
      unlockScore: 500,
      area: '日南市飫肥地区',
      headline: '魚のすり身に豆腐を混ぜて揚げる、やわらかい揚げかまぼこ。',
      tip: '揚げたてを何もつけずそのまま。黒砂糖の甘みが出るのでおやつにもなる。',
      links: searchLinks('日南市', 'おび天'),
    },
  },
  {
    id: 'hyuganatsu',
    level: 3,
    name: '日向夏',
    shortName: '日向夏',
    image: '/assets/foods/miyazaki/03_hyuganatsu.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '白いところを、残して食べる。',
    source: 'https://www.pref.miyazaki.lg.jp/contents/org/honbu/hisho/jaja/16_ippin.html',
    icon: { label: '日向夏', shape: 'berry', baseColor: '#f0d048', accentColor: '#f4f0e0' },
    reward: {
      unlockScore: 1000,
      area: '宮崎市（赤江で見つかったと伝わる）',
      headline: '白い内皮ごと食べるかんきつ。文政年間ごろ、宮崎市赤江で1本の木として見つかったと伝わる。',
      tip: '外皮をなるべく薄くむき、白い内皮をたくさん残す。白い部分に苦みがなく、果肉と一緒に食べる。',
      links: searchLinks('宮崎県', '日向夏'),
    },
  },
  {
    id: 'gane',
    level: 4,
    name: 'がね',
    shortName: 'がね',
    image: '/assets/foods/miyazaki/04_gane.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '姿が、カニに似ている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/gane_miyazaki.html',
    icon: { label: 'がね', shape: 'dumpling', baseColor: '#d8a040', accentColor: '#8a6a2a' },
    reward: {
      unlockScore: 1800,
      area: '県央地方（霧島山麓ほか）',
      headline: 'さつまいもと野菜を細切りにして揚げる。姿がカニに似ていることから「がね」と呼ぶ。',
      tip: '子どもも大人も、おやつやおかずに。味つけで甘くも塩気にもなる。',
      links: searchLinks('宮崎県', 'がね'),
    },
  },
  {
    id: 'miyazaki-sumibiyaki',
    level: 5,
    name: '鶏の炭火焼（もも焼き）',
    shortName: '鶏の炭火焼',
    image: '/assets/foods/miyazaki/05_miyazaki_sumibiyaki.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '塩こしょうなどで下味をつけた鶏を、高温の炭火であぶり焼きにする。専門店のほか居酒屋のメニューにも並ぶ宮崎の定番のご当地グルメ。親鳥を使った歯ごたえのあるものもある。',
    source:
      'https://www.kanko-miyazaki.jp/gourmet/meat',
    icon: { label: '炭火焼', shape: 'feast', baseColor: '#2a2420', accentColor: '#6a9a3a' },
    reward: {
      unlockScore: 3000,
      area: '宮崎市ほか県内全域',
      headline: '高温の炭火であぶる鶏',
      tip: '昔は農家の多くが家で鶏を飼い、人が集まるときにさばいて食べた。半分に切ったドラム缶に網をのせ、炭火で真っ黒に焼き上げるのが定番とされる。',
      links: searchLinks('宮崎市', '鶏の炭火焼'),
      official: [{ label: '農林水産省「うちの郷土料理」九州地方 宮崎県', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/area_stories/miyazaki.html' }],
    },
  },
  {
    id: 'mehikari',
    level: 6,
    name: 'めひかりの唐揚げ',
    shortName: 'めひかり',
    image: '/assets/foods/miyazaki/06_mehikari.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '目が光る、小さな深海魚。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/mehikari_no_karaage_miyazaki.html',
    icon: { label: 'めひかり', shape: 'fish', baseColor: '#d09040', accentColor: '#6a8a9a' },
    reward: {
      unlockScore: 4500,
      area: '延岡市',
      headline: '目が大きく青く光って見える小さな深海魚。骨がやわらかく丸ごと食べられる。',
      tip: '頭と内臓を取って粉をまぶし、180度で揚げる。',
      links: searchLinks('延岡市', 'めひかり'),
    },
  },
  {
    id: 'karamen',
    level: 7,
    name: '辛麺',
    shortName: '辛麺',
    image: '/assets/foods/miyazaki/07_karamen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '辛さは、小辛から選べる。',
    source: 'https://nobekan.jp/feature/index.php?p=karamen',
    icon: { label: '辛麺', shape: 'ramen', baseColor: '#c0301a', accentColor: '#f0d848' },
    reward: {
      unlockScore: 6500,
      area: '延岡市',
      headline: '真っ赤なスープに卵とにんにくと挽肉。通称「こんにゃく麺」と呼ばれる独特の食感の麺。',
      tip: '辛さを小辛からスーパー激辛まで選べる。',
      links: searchLinks('延岡市', '辛麺'),
    },
  },
  {
    id: 'chicken-nanban',
    level: 8,
    name: 'チキン南蛮',
    shortName: 'チキン南蛮',
    image: '/assets/foods/miyazaki/08_chicken_nanban.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '揚げてすぐ、甘酢にくぐらせる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/chikin_nanban_miyazaki.html',
    icon: { label: 'チキン南蛮', shape: 'dumpling', baseColor: '#d8a040', accentColor: '#f4f0e0' },
    reward: {
      unlockScore: 9000,
      area: '延岡市／宮崎県内全域',
      headline: '揚げた鶏肉を甘酢にくぐらせ、タルタルソースをかける。昭和30年代に延岡で生まれたとされる。',
      tip: 'タルタルをかける型と、甘酢だけの型の2通りがある。',
      links: searchLinks('宮崎県', 'チキン南蛮'),
    },
  },
  {
    id: 'hiyajiru',
    level: 9,
    name: '冷や汁',
    shortName: '冷や汁',
    image: '/assets/foods/miyazaki/09_hiyajiru.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '冷やした汁を、温かい麦飯へ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/hiyajiru_miyazaki.html',
    icon: { label: '冷や汁', shape: 'pot', baseColor: '#d8cba8', accentColor: '#6a8a4a' },
    reward: {
      unlockScore: 12000,
      area: '県央地方',
      headline: 'いりこ・味噌・ごまをすり合わせ、冷やして麦飯にかける。',
      tip: 'すった汁をいったん冷やしてから、温かい麦飯にかける。きゅうり・青じそ・みょうがを添える。',
      links: searchLinks('宮崎県', '冷や汁'),
    },
  },
  {
    id: 'kappo-dori',
    level: 10,
    name: 'かっぽ鶏',
    shortName: 'かっぽ鶏',
    image: '/assets/foods/miyazaki/10_kappo_dori.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '青竹の筒ごと、炭火にかける。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kappo_dori_miyazaki.html',
    icon: { label: 'かっぽ鶏', shape: 'hotpot', baseColor: '#7a9a3a', accentColor: '#d8a040' },
    reward: {
      unlockScore: 16000,
      area: '高千穂町',
      headline: '青竹の筒に鶏肉と野菜を詰め、炭火でじっくり蒸し焼きにする。',
      tip: '竹の節と節の間をくり抜いて具を詰める。中で蒸されるようにゆっくり火が通る。',
      links: searchLinks('高千穂町', 'かっぽ鶏'),
    },
  },
  {
    id: 'taiyo-no-tamago',
    level: 11,
    name: '完熟マンゴー 太陽のタマゴ',
    shortName: '太陽のタマゴ',
    image: '/assets/foods/miyazaki/11_taiyo_no_tamago.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '落ちた実を、ネットで受け止める。',
    source: 'https://www.pref.miyazaki.lg.jp/contents/org/shoko/appeal/jaja/17_13.html',
    icon: { label: '太陽のタマゴ', shape: 'berry', baseColor: '#d8402a', accentColor: '#f0c030' },
    reward: {
      unlockScore: 22000,
      area: '宮崎県内（施設栽培）',
      headline: '樹の上で完熟し、自然に落ちた実をネットで受け止めて収穫する。手もぎではない。',
      tip: '色・形・大きさなどの基準を満たしたものだけがこの名で出荷される。',
      links: searchLinks('宮崎県', '完熟マンゴー'),
    },
  },
  {
    id: 'miyazaki-gyu',
    level: 12,
    name: '宮崎牛',
    shortName: '宮崎牛',
    image: '/assets/foods/miyazaki/12_miyazaki_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '4等級以上だけが、名乗れる。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/55.html',
    icon: { label: '宮崎牛', shape: 'feast', baseColor: '#c0443a', accentColor: '#f0e0d0' },
    reward: {
      unlockScore: 30000,
      area: '宮崎県内全域',
      headline: 'GI登録の黒毛和種。肉質等級4等級以上で、県内で生まれ県内での飼養期間が最も長いもの。',
      tip: '全国和牛能力共進会で史上初の4大会連続の内閣総理大臣賞を受けている。',
      links: searchLinks('宮崎県', '宮崎牛'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const miyazakiFoodStage: StageDefinition = {
  regionId: 'miyazaki',
  regionName: '宮崎県',
  mode: 'food',
  title: '宮崎めしマージ',
  theme: {
    primaryColor: '#b8621f',
    secondaryColor: '#d9a441',
    accentColor: '#f0c860',
    backgroundColor: '#1a1512',
  },
  /** 背景ツアー「宮崎めぐり」。軸は時間帯。日向岬の夜明けから都井岬の日没へ。 */
  scenery: {
    tour: miyazakiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, MIYAZAKI_FOOD_HULLS),
};
